import { useState, useMemo } from "react";
import { useAtomValue } from "jotai";
import type { ExecutionBusinessLine } from "@monitoring-panel/types";
import {
  Calendar,
  DollarSign,
  TrendingUp,
  Target,
  ChevronDown,
  ChevronUp,
  Bug,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  Filter,
} from "lucide-react";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { useGlobalRevenue } from "@/hooks/useRevenue";
import { formatNumber, formatDate } from "@/lib/utils";
import { MetricBox } from "@/components/MetricBox";
import { HoldingsBarChart } from "@/components/HoldingsBarChart";
import { HoldingsSunburstChart } from "@/components/HoldingsSunburstChart";
import { OpportunityTypePieChart } from "@/components/OpportunityTypePieChart";
import { BusinessLinesTable, marginTextClass } from "@/components/BusinessLinesTable";
import { RevenueDateRangeBar } from "@/components/revenue/RevenueDateRangeBar";
import { revenueDateRangeAtom } from "@/store/revenue";
import {
  ADVIA_CHART_OPTS,
  ADVIA_ECHARTS_THEME,
  REVENUE_COLORS,
  solid,
  tooltipRow,
  tooltipTitle,
} from "@/lib/advia-charts";

// Served, closing and forecast stack into one bar: a day in progress carries
// the revenue of the executions already loaded plus the forecast of the ones
// still pending, and both have to be readable at once.
const CLOSING_CHART_COLORS = {
  served: solid(REVENUE_COLORS.served),
  closing: solid(REVENUE_COLORS.closing),
  forecast: solid(REVENUE_COLORS.forecast),
  margin: solid(REVENUE_COLORS.margin),
} as const;


function targetColor(percentage: number): string {
  if (percentage >= 100) return "text-adv-success";
  if (percentage >= 80) return "text-adv-accent";
  return "text-adv-warning";
}

interface GlobalRevenueViewProps {
  scope: ExecutionBusinessLine;
}

/**
 * Revenue of an execution-based business line: Display & Video or ChatGPT
 * Ads. Both are measured on their executions' delivery the same way.
 */
export function GlobalRevenueView({ scope }: GlobalRevenueViewProps) {
  const { startDate, endDate } = useAtomValue(revenueDateRangeAtom);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [showDailyTable, setShowDailyTable] = useState(false);

  // Debug table state
  const [debugPage, setDebugPage] = useState(1);
  const [debugPageSize, setDebugPageSize] = useState(10);
  const [debugSortField, setDebugSortField] = useState<string>("totalRevenue");
  const [debugSortDirection, setDebugSortDirection] = useState<"asc" | "desc">(
    "desc",
  );
  const [debugTypeFilter, setDebugTypeFilter] = useState<
    "all" | "Deal" | "Managed"
  >("all");
  const [debugSearchTerm, setDebugSearchTerm] = useState("");

  const { data, isLoading, error } = useGlobalRevenue(
    startDate,
    endDate,
    scope,
    shouldFetch,
  );

  const handleLoadData = () => {
    setShouldFetch(true);
  };

  // Process and filter debug executions
  const processedDebugExecutions = useMemo(() => {
    if (!data?.debugExecutions) return [];

    let filtered = [...data.debugExecutions];

    // Apply type filter
    if (debugTypeFilter !== "all") {
      filtered = filtered.filter(
        (exec) => exec.executionType === debugTypeFilter,
      );
    }

    // Apply search filter
    if (debugSearchTerm) {
      const searchLower = debugSearchTerm.toLowerCase();
      filtered = filtered.filter(
        (exec) =>
          exec.executionId.toLowerCase().includes(searchLower) ||
          exec.executionName.toLowerCase().includes(searchLower) ||
          exec.status.toLowerCase().includes(searchLower),
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[debugSortField as keyof typeof a];
      let bValue: any = b[debugSortField as keyof typeof b];
      if (aValue === null) aValue = Number.NEGATIVE_INFINITY;
      if (bValue === null) bValue = Number.NEGATIVE_INFINITY;

      // Handle numeric fields
      if (typeof aValue === "number" && typeof bValue === "number") {
        return debugSortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      // Handle string fields
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (debugSortDirection === "asc") {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });

    return filtered;
  }, [
    data?.debugExecutions,
    debugTypeFilter,
    debugSearchTerm,
    debugSortField,
    debugSortDirection,
  ]);

  // Paginate debug executions
  const paginatedDebugExecutions = useMemo(() => {
    const startIndex = (debugPage - 1) * debugPageSize;
    const endIndex = startIndex + debugPageSize;
    return processedDebugExecutions.slice(startIndex, endIndex);
  }, [processedDebugExecutions, debugPage, debugPageSize]);

  // Totals over the filtered set
  const debugTotals = useMemo(() => {
    return processedDebugExecutions.reduce(
      (acc, exec) => {
        acc.advertiserBudget += exec.advertiserBudget || 0;
        acc.actualRevenue += exec.actualRevenue || 0;
        acc.mediaCost += exec.mediaCost || 0;
        acc.servedRevenue += exec.servedRevenue || 0;
        acc.closingBudget += exec.closingBudget || 0;
        acc.forecastedRevenue += exec.forecastedRevenue || 0;
        acc.totalRevenue += exec.totalRevenue || 0;
        acc.remainingBudget += exec.remainingBudget || 0;
        return acc;
      },
      {
        advertiserBudget: 0,
        actualRevenue: 0,
        mediaCost: 0,
        servedRevenue: 0,
        closingBudget: 0,
        forecastedRevenue: 0,
        totalRevenue: 0,
        remainingBudget: 0,
      },
    );
  }, [processedDebugExecutions]);

  // Margin of the filtered set as a whole, weighted by revenue
  const debugMarginTotal =
    debugTotals.actualRevenue > 0
      ? ((debugTotals.actualRevenue - debugTotals.mediaCost) / debugTotals.actualRevenue) * 100
      : 0;

  const totalDebugPages = Math.ceil(
    processedDebugExecutions.length / debugPageSize,
  );

  const handleSort = (field: string) => {
    if (debugSortField === field) {
      setDebugSortDirection(debugSortDirection === "asc" ? "desc" : "asc");
    } else {
      setDebugSortField(field);
      setDebugSortDirection("desc");
    }
    setDebugPage(1); // Reset to first page when sorting
  };

  const chartOption: EChartsOption = useMemo(() => {
    if (!data?.data || data.data.length === 0) return {};

    const dates = data.data.map((row) => formatDate(row.date));
    // `revenue` is the official figure per day; the part backed by a closing
    // budget gets its own colour so a closed campaign reads differently from
    // one still measured on delivery.
    const servedRevenueData = data.data.map(
      (row) => row.revenue - row.closedRevenue,
    );
    const closedRevenueData = data.data.map((row) => row.closedRevenue);
    const forecastedRevenueData = data.data.map((row) => row.forecastedRevenue);
    const marginPercentageData = data.data.map((row) =>
      row.revenue > 0
        ? ((row.revenue - row.totalMediaCost) / row.revenue) * 100
        : 0,
    );

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
        },
        formatter: (params: unknown) => {
          const items = params as Array<{
            seriesName: string;
            value: number;
            marker: string;
            axisValue: string;
          }>;
          let result = tooltipTitle(items[0].axisValue);
          items.forEach((item) => {
            if (item.value > 0 || item.seriesName === "Margin %") {
              const formattedValue =
                item.seriesName === "Margin %"
                  ? formatNumber(item.value, "percentage", 2)
                  : formatNumber(item.value, "euro");
              result += tooltipRow(item.seriesName, formattedValue, item.marker);
            }
          });
          return result;
        },
      },
      // containLabel already reserves room for the axis labels, so the margins
      // only need to clear the legend on top and the axis names on the sides.
      grid: {
        right: 16,
        left: 16,
        bottom: 8,
        top: 48,
        containLabel: true,
      },
      legend: {
        data: [
          "Served Revenue",
          "Closing Revenue",
          "Forecasted Revenue",
          "Margin %",
        ],
        top: 0,
        left: "center",
        itemGap: 24,
      },
      xAxis: [
        {
          type: "category",
          data: dates,
          axisTick: {
            alignWithLabel: true,
          },
          axisLabel: {
            margin: 10,
            hideOverlap: true,
          },
        },
      ],
      yAxis: [
        {
          type: "value",
          name: "Revenue (€)",
          position: "left",
          axisLabel: {
            formatter: (value: number) => formatNumber(value, "euro"),
          },
        },
        {
          type: "value",
          name: "Margin %",
          position: "right",
          axisLabel: {
            formatter: (value: number) => formatNumber(value, "percentage", 0),
          },
          splitLine: {
            show: false,
          },
          min: 0,
          max: 100,
        },
      ],
      series: [
        {
          name: "Served Revenue",
          type: "bar",
          stack: "official",
          data: servedRevenueData,
          barWidth: "90%",
          itemStyle: {
            color: CLOSING_CHART_COLORS.served,
          },
        },
        {
          name: "Closing Revenue",
          type: "bar",
          stack: "official",
          data: closedRevenueData,
          barWidth: "90%",
          itemStyle: {
            color: CLOSING_CHART_COLORS.closing,
          },
        },
        {
          name: "Forecasted Revenue",
          type: "bar",
          stack: "official",
          data: forecastedRevenueData,
          barWidth: "90%",
          itemStyle: {
            color: CLOSING_CHART_COLORS.forecast,
            borderRadius: [4, 4, 0, 0],
          },
        },
        {
          name: "Margin %",
          type: "line",
          yAxisIndex: 1,
          data: marginPercentageData,
          smooth: true,
          itemStyle: {
            color: CLOSING_CHART_COLORS.margin,
          },
          lineStyle: {
            width: 1.5,
            color: CLOSING_CHART_COLORS.margin,
          },
          symbolSize: 6,
        },
      ],
    };
  }, [data]);

  return (
    <div className="space-y-6">
      <RevenueDateRangeBar
        isLoading={isLoading}
        onLoad={handleLoadData}
        onRangeChange={() => setShouldFetch(false)}
        footer={data && `${data.campaignsIncluded} campaigns included in range`}
      />

      {error && (
        <div className="adv-alert">
          <p>Error: {(error as Error).message}</p>
        </div>
      )}

      {data?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <MetricBox
            title="Acc Revenue"
            value={data.summary.accumulatedRevenue}
            subtitle="Actual revenue (no forecast)"
            format="euro"
            icon={DollarSign}
          />
          <MetricBox
            title="Revenue Estimated"
            value={data.summary.accumulatedPlusForecasted}
            subtitle={`Includes ${formatNumber(data.summary.totalForecasted, "euro")} forecasted`}
            format="euro"
            icon={TrendingUp}
          />
          <MetricBox
            title="Target vs Actual + Forecast"
            value={data.summary.targetVsActualAndForecast.percentage}
            subtitle={`${formatNumber(data.summary.targetVsActualAndForecast.amount, "euro")} / ${formatNumber(data.summary.targetVsActualAndForecast.target, "euro")}`}
            format="percentage"
            icon={Target}
            valueColor={targetColor(data.summary.targetVsActualAndForecast.percentage)}
          />
          <MetricBox
            title="Managed Revenue"
            value={data.summary.managedRevenue.total}
            subtitle={`Actual: ${formatNumber(data.summary.managedRevenue.actual, "euro")} + Forecast: ${formatNumber(data.summary.managedRevenue.forecasted, "euro")}`}
            format="euro"
            icon={DollarSign}
          />
          <MetricBox
            title="Deal Revenue"
            value={data.summary.dealRevenue.total}
            subtitle={`Actual: ${formatNumber(data.summary.dealRevenue.actual, "euro")} + Forecast: ${formatNumber(data.summary.dealRevenue.forecasted, "euro")}`}
            format="euro"
            icon={DollarSign}
          />
        </div>
      )}

      {data && data.data.length > 0 && (
        <div className="adv-card p-6">
          <h2 className="adv-section-title mb-6">
            Daily Revenue (Actual & Forecasted)
          </h2>
          <ReactECharts
            option={chartOption}
            style={{ height: 560 }}
            theme={ADVIA_ECHARTS_THEME}
            opts={ADVIA_CHART_OPTS}
          />
        </div>
      )}

      {data?.businessLineSegments && data.businessLineSegments.length > 0 && (
        <BusinessLinesTable segments={data.businessLineSegments} />
      )}

      {/* Branding / Performance / Youtube only classify Display & Video products */}
      {scope === "display-video" && data?.summary?.opportunityTypeBreakdown && (
        <OpportunityTypePieChart data={data.summary.opportunityTypeBreakdown} />
      )}

      {data?.holdingsBreakdown && data.holdingsBreakdown.length > 0 && (
        <>
          <HoldingsBarChart data={data.holdingsBreakdown} />
          <HoldingsSunburstChart data={data.holdingsBreakdown} showForecasted={true} />
        </>
      )}

      {data?.debugExecutions && data.debugExecutions.length > 0 && (
        <div className="adv-card adv-card--featured overflow-hidden">
          <button
            onClick={() => setShowDebug(!showDebug)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-adv-sunken transition-colors focus-visible:outline-none focus-visible:shadow-adv-focus"
          >
            <div className="flex items-center gap-3">
              <Bug className="text-adv-accent" size={20} />
              <div className="text-left">
                <h2 className="adv-section-title">
                  Debug: Execution Details
                </h2>
                <p className="adv-section-desc mt-0.5">
                  All {data.debugExecutions.length} executions with revenue
                  breakdown, forecasts, and budget tracking
                </p>
              </div>
            </div>
            {showDebug ? (
              <ChevronUp className="text-adv-tertiary" size={20} />
            ) : (
              <ChevronDown className="text-adv-tertiary" size={20} />
            )}
          </button>

          {showDebug && (
            <div className="border-t border-adv-border">
              {/* Filters and Search */}
              <div className="p-4 bg-adv-sunken border-b border-adv-border flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-adv-tertiary pointer-events-none"
                      size={18}
                    />
                    <input
                      type="text"
                      placeholder="Search by ID, name, or status..."
                      value={debugSearchTerm}
                      onChange={(e) => {
                        setDebugSearchTerm(e.target.value);
                        setDebugPage(1);
                      }}
                      className="adv-input adv-input--sm w-full pl-10 pr-4"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Filter className="text-adv-tertiary" size={18} />
                  <select
                    value={debugTypeFilter}
                    onChange={(e) => {
                      setDebugTypeFilter(
                        e.target.value as "all" | "Deal" | "Managed",
                      );
                      setDebugPage(1);
                    }}
                    className="adv-input adv-input--sm"
                  >
                    <option value="all">All Types</option>
                    <option value="Deal">Deal</option>
                    <option value="Managed">Managed</option>
                  </select>
                </div>

                <div className="text-adv-body-s text-adv-secondary">
                  Showing {paginatedDebugExecutions.length} of{" "}
                  {processedDebugExecutions.length} executions
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="adv-table adv-table--compact">
                  <thead className="bg-adv-sunken">
                    <tr>
                      {[
                        {
                          field: "executionId",
                          label: "Execution ID",
                          align: "left",
                        },
                        {
                          field: "executionName",
                          label: "Name",
                          align: "left",
                        },
                        {
                          field: "executionType",
                          label: "Type",
                          align: "center",
                        },
                        { field: "status", label: "Status", align: "center" },
                        { field: "startDate", label: "Dates", align: "center" },
                        {
                          field: "advertiserBudget",
                          label: "Budget",
                          align: "right",
                        },
                        {
                          field: "actualRevenue",
                          label: "Actual Revenue",
                          align: "right",
                        },
                        {
                          field: "servedRevenue",
                          label: "Served",
                          align: "right",
                        },
                        {
                          field: "closingBudget",
                          label: "Closing",
                          align: "right",
                        },
                        {
                          field: "closingDeviation",
                          label: "Deviation",
                          align: "right",
                        },
                        {
                          field: "mediaCost",
                          label: "Cost",
                          align: "right",
                        },
                        {
                          field: "margin",
                          label: "Margin",
                          align: "right",
                        },
                        {
                          field: "forecastedRevenue",
                          label: "Forecasted",
                          align: "right",
                        },
                        {
                          field: "totalRevenue",
                          label: "Total",
                          align: "right",
                        },
                        {
                          field: "remainingBudget",
                          label: "Remaining",
                          align: "right",
                        },
                        {
                          field: "isInRange",
                          label: "In Range",
                          align: "center",
                        },
                      ].map(({ field, label, align }) => (
                        <th
                          key={field}
                          onClick={() => handleSort(field)}
                          className={`px-4 py-3 text-${align} cursor-pointer hover:text-adv-primary select-none`}
                        >
                          <div
                            className={`flex items-center gap-1 ${align === "right" ? "justify-end" : align === "center" ? "justify-center" : ""}`}
                          >
                            {label}
                            {debugSortField === field &&
                              (debugSortDirection === "asc" ? (
                                <ArrowUp size={14} className="text-adv-accent" />
                              ) : (
                                <ArrowDown
                                  size={14}
                                  className="text-adv-accent"
                                />
                              ))}
                            {debugSortField !== field && (
                              <ArrowUpDown
                                size={14}
                                className="text-adv-tertiary opacity-0 group-hover:opacity-100"
                              />
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDebugExecutions.map((exec) => (
                      <tr
                        key={exec.executionId}
                        className={`hover:bg-adv-sunken ${!exec.hasCachedData ? "bg-adv-gold-crema" : !exec.hasRemainingBudget ? "bg-adv-warning-bg" : exec.forecastedRevenue > 0 ? "bg-adv-success-bg" : ""}`}
                      >
                        <td className="px-4 py-3 whitespace-nowrap adv-num">
                          {exec.executionId}
                        </td>
                        <td className="px-4 py-3 max-w-xs truncate">
                          {exec.executionName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span
                            className={`adv-tag ${
                              exec.executionType === "Deal"
                                ? "adv-tag--info"
                                : "adv-tag--default"
                            }`}
                          >
                            {exec.executionType}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-adv-secondary text-center">
                          {exec.status}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap adv-num text-adv-secondary text-center">
                          {formatDate(exec.startDate)}
                          <br />→ {formatDate(exec.endDate)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap adv-num text-right">
                          {formatNumber(exec.advertiserBudget, "euro")}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap adv-num text-right font-bold">
                          {formatNumber(exec.actualRevenue, "euro")}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap adv-num text-adv-secondary text-right">
                          {formatNumber(exec.servedRevenue, "euro")}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap adv-num text-right">
                          {exec.closingBudget === null ? (
                            <span className="text-adv-tertiary">—</span>
                          ) : (
                            <span className="font-bold">
                              {formatNumber(exec.closingBudget, "euro")}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap adv-num text-right">
                          {!exec.hasCachedData ? (
                            <span
                              className="adv-tag adv-tag--warning font-adv-sans"
                              title="No hay caché en GCS para esta execution: su closing budget no aparece en el gráfico diario. Refresca la caché (POST /api/cache/refresh/{executionId})."
                            >
                              sin caché
                            </span>
                          ) : exec.closingDeviation === null ? (
                            <span className="text-adv-tertiary">—</span>
                          ) : (
                            <span
                              className={
                                exec.closingDeviation < 0
                                  ? "text-adv-warning-text font-bold"
                                  : exec.closingDeviation > 0
                                    ? "text-adv-success font-bold"
                                    : "text-adv-secondary"
                              }
                            >
                              {exec.closingDeviation > 0 ? "+" : ""}
                              {formatNumber(
                                exec.closingDeviation,
                                "percentage",
                                1,
                              )}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap adv-num text-right">
                          {formatNumber(exec.mediaCost, "euro")}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap adv-num text-right">
                          {exec.margin === null ? (
                            <span className="text-adv-tertiary">—</span>
                          ) : (
                            <span className={`font-bold ${marginTextClass(exec.margin)}`}>
                              {formatNumber(exec.margin, "percentage", 1)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap adv-num text-right">
                          <span
                            className={
                              exec.forecastedRevenue > 0
                                ? "text-adv-success font-bold"
                                : "text-adv-tertiary"
                            }
                          >
                            {formatNumber(exec.forecastedRevenue, "euro")}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap adv-num font-bold text-right">
                          {formatNumber(exec.totalRevenue, "euro")}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap adv-num text-right">
                          <span
                            className={
                              exec.hasRemainingBudget
                                ? "text-adv-success"
                                : "text-adv-warning-text font-bold"
                            }
                          >
                            {formatNumber(exec.remainingBudget, "euro")}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {exec.isInRange ? (
                            <span className="text-adv-success">✓</span>
                          ) : (
                            <span className="text-adv-tertiary">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="sticky bottom-0">
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-3 text-adv-label font-bold text-adv-secondary text-right uppercase tracking-wide"
                      >
                        Total ({processedDebugExecutions.length}{" "}
                        {processedDebugExecutions.length === 1
                          ? "execution"
                          : "executions"}
                        )
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap adv-num font-bold text-right">
                        {formatNumber(debugTotals.advertiserBudget, "euro")}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap adv-num font-bold text-right">
                        {formatNumber(debugTotals.actualRevenue, "euro")}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap adv-num font-bold text-adv-secondary text-right">
                        {formatNumber(debugTotals.servedRevenue, "euro")}
                      </td>
                      <td
                        className="px-4 py-3 whitespace-nowrap adv-num font-bold text-right"
                      >
                        {formatNumber(debugTotals.closingBudget, "euro")}
                      </td>
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3 whitespace-nowrap adv-num font-bold text-right">
                        {formatNumber(debugTotals.mediaCost, "euro")}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap adv-num font-bold text-right">
                        {debugTotals.actualRevenue > 0 ? (
                          <span className={marginTextClass(debugMarginTotal)}>
                            {formatNumber(debugMarginTotal, "percentage", 1)}
                          </span>
                        ) : (
                          <span className="text-adv-tertiary">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap adv-num font-bold text-right">
                        <span
                          className={
                            debugTotals.forecastedRevenue > 0
                              ? "text-adv-success"
                              : "text-adv-tertiary"
                          }
                        >
                          {formatNumber(debugTotals.forecastedRevenue, "euro")}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap adv-num font-bold text-right">
                        {formatNumber(debugTotals.totalRevenue, "euro")}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap adv-num font-bold text-right">
                        <span
                          className={
                            debugTotals.remainingBudget >= 0
                              ? "text-adv-success"
                              : "text-adv-warning-text"
                          }
                        >
                          {formatNumber(debugTotals.remainingBudget, "euro")}
                        </span>
                      </td>
                      <td className="px-4 py-3" />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalDebugPages > 1 && (
                <div className="px-4 py-3 bg-adv-sunken border-t border-adv-border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-adv-body-s text-adv-secondary">
                      Rows per page:
                    </span>
                    <select
                      value={debugPageSize}
                      onChange={(e) => {
                        setDebugPageSize(Number(e.target.value));
                        setDebugPage(1);
                      }}
                      className="adv-input adv-input--sm adv-num"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-adv-body-s text-adv-secondary">
                      Page <span className="adv-num">{debugPage}</span> of{" "}
                      <span className="adv-num">{totalDebugPages}</span>
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setDebugPage(1)}
                        disabled={debugPage === 1}
                        className="adv-btn adv-btn--secondary adv-btn--sm"
                      >
                        First
                      </button>
                      <button
                        onClick={() => setDebugPage(debugPage - 1)}
                        disabled={debugPage === 1}
                        className="adv-btn adv-btn--secondary adv-btn--sm"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setDebugPage(debugPage + 1)}
                        disabled={debugPage === totalDebugPages}
                        className="adv-btn adv-btn--secondary adv-btn--sm"
                      >
                        Next
                      </button>
                      <button
                        onClick={() => setDebugPage(totalDebugPages)}
                        disabled={debugPage === totalDebugPages}
                        className="adv-btn adv-btn--secondary adv-btn--sm"
                      >
                        Last
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {data && data.data.length > 0 && (
        <div className="adv-card adv-card--featured overflow-hidden">
          <button
            onClick={() => setShowDailyTable(!showDailyTable)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-adv-sunken transition-colors focus-visible:outline-none focus-visible:shadow-adv-focus"
          >
            <div className="flex items-center gap-3">
              <Calendar className="text-adv-accent" size={20} />
              <div className="text-left">
                <h2 className="adv-section-title">
                  Daily Revenue Breakdown
                </h2>
                <p className="adv-section-desc mt-0.5">
                  Complete daily breakdown for all {data.data.length} days in
                  the selected date range
                </p>
              </div>
            </div>
            {showDailyTable ? (
              <ChevronUp className="text-adv-tertiary" size={20} />
            ) : (
              <ChevronDown className="text-adv-tertiary" size={20} />
            )}
          </button>

          {showDailyTable && (
            <div className="border-t border-adv-border">
              <div className="overflow-x-auto">
                <table className="adv-table">
                  <thead className="bg-adv-sunken">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        Date
                      </th>
                      <th className="px-6 py-3 text-right">
                        Actual Revenue
                      </th>
                      <th className="px-6 py-3 text-right">
                        of which Closing
                      </th>
                      <th className="px-6 py-3 text-right">
                        Forecasted Revenue
                      </th>
                      <th className="px-6 py-3 text-right">
                        Total
                      </th>
                      <th className="px-6 py-3 text-right">
                        Daily Goal
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((row) => (
                      <tr key={row.date} className="hover:bg-adv-sunken">
                        <td className="px-6 py-3 whitespace-nowrap adv-num">
                          {formatDate(row.date)}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap adv-num text-right">
                          {formatNumber(row.revenue, "euro")}
                        </td>
                        <td
                          className="px-6 py-3 whitespace-nowrap adv-num text-right"
                        >
                          {formatNumber(row.closedRevenue, "euro")}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap adv-num text-adv-secondary text-right">
                          {formatNumber(row.forecastedRevenue, "euro")}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap adv-num font-bold text-right">
                          {formatNumber(
                            row.revenue + row.forecastedRevenue,
                            "euro",
                          )}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap adv-num text-adv-secondary text-right">
                          {formatNumber(row.dailyGoal, "euro")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="px-6 py-3 whitespace-nowrap font-bold">
                        Total
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap adv-num font-bold text-right">
                        {data.summary &&
                          formatNumber(data.summary.accumulatedRevenue, "euro")}
                      </td>
                      <td
                        className="px-6 py-3 whitespace-nowrap adv-num font-bold text-right"
                      >
                        {data.summary &&
                          formatNumber(
                            data.summary.accumulatedClosedRevenue,
                            "euro",
                          )}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap adv-num font-bold text-adv-secondary text-right">
                        {data.summary &&
                          formatNumber(data.summary.totalForecasted, "euro")}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap adv-num font-bold text-right">
                        {data.summary &&
                          formatNumber(
                            data.summary.accumulatedPlusForecasted,
                            "euro",
                          )}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap adv-num font-bold text-adv-secondary text-right">
                        {data.summary &&
                          formatNumber(data.summary.targetRevenue, "euro")}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {data && data.data.length === 0 && (
        <div className="adv-card adv-state">
          <div className="adv-state__icon">
            <DollarSign size={26} />
          </div>
          <p className="adv-state__desc">
            No data found for the selected date range
          </p>
        </div>
      )}

      {!shouldFetch && !data && (
        <div className="adv-card adv-state">
          <div className="adv-state__icon">
            <Calendar size={26} />
          </div>
          <p className="adv-state__desc">
            Select a date range and click "Load Data" to view revenue data
          </p>
        </div>
      )}
    </div>
  );
}
